import { Chip, Grid } from "@mui/material";
import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import UiCard from "components/base/UiCard";
import UiTypography from "components/base/UiTypography";
import { Proposal } from "generated/graphql";
import { CustomGetProposalQueryData } from "hooks/proposals/useCustomGetProposal";
import useModalState from "hooks/useModalState";
import Modal from "modules/Modal/Modal";
import ProposalStageSummary from "modules/proposals/ProposalStageSummary";
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { headerCase } from "change-case";
import { useDraftContext } from "./DraftContext";
import ConfirmProposalModal from "./ConfirmProposalModal";
import PreviewProposalButton from "./PreviewProposalButton";
import ClientInfoCard from "./ClientInfoCard";
import FinancialsCard from "./FinancialsCard";
import { draftColors } from "./draftDesignTokens";

type TopPanelProps = {
  proposal: NonNullable<CustomGetProposalQueryData>;
};

export default function TopPanel({ proposal }: TopPanelProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const summaryModal = useModalState();

  const openSendModal = useCallback(() => setModalOpen(true), []);
  const closeSendModal = useCallback(() => setModalOpen(false), []);

  const status = proposal.status ? headerCase(proposal.status) : "Draft";
  const { isReadOnly } = useDraftContext();

  return (
    <>
      <UiCard
        variant="panel"
        sx={{ p: "16px", display: "flex", flexDirection: "column", gap: "12px" }}
      >
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap="8px"
        >
          <MDBox display="flex" alignItems="center" gap="12px" flexWrap="wrap">
            <Link
              to={`/proposals/${proposal.id}/info`}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <UiTypography
                uiVariant="heading-lg"
                sx={{ fontSize: "18px", "&:hover": { textDecoration: "underline" } }}
              >
                Proposal {proposal.externalIdWithPrefix ?? ""}
              </UiTypography>
            </Link>
            <Chip
              label={status}
              size="small"
              sx={{
                backgroundColor: draftColors.chipBg,
                color: draftColors.chipText,
                fontWeight: 600,
                fontSize: "12px",
                px: "8px",
                py: "4px",
                height: "auto",
                borderRadius: "6px",
              }}
            />
          </MDBox>
          <MDBox display="flex" gap="8px" flexShrink={0}>
            <UiButton uiVariant="primary-grey" onClick={summaryModal.onOpen}>
              Proposal Summary
            </UiButton>
            <PreviewProposalButton proposal={proposal as Proposal} />
            {!isReadOnly && (
              <UiButton uiVariant="primary-green" onClick={openSendModal}>
                Send Proposal
              </UiButton>
            )}
          </MDBox>
        </MDBox>

        <Grid container spacing="12px">
          <Grid item xs={12} lg={4}>
            <ClientInfoCard proposal={proposal} />
          </Grid>
          <Grid item xs={12} lg={8}>
            <FinancialsCard proposal={proposal} />
          </Grid>
        </Grid>
      </UiCard>

      <ConfirmProposalModal
        open={modalOpen}
        onClose={closeSendModal}
        proposal={proposal as Proposal}
        initialAction="send"
      />

      <Modal
        open={summaryModal.open}
        onClose={summaryModal.onClose}
        styles={{ overflow: "auto", maxWidth: "auto" }}
      >
        <ProposalStageSummary onCloseButton={summaryModal.onClose} />
      </Modal>
    </>
  );
}
